import { calculateExcelColumnWidths, createExcelSheetData, downloadExcelFile } from '../../shared/excelExport.ts'
import { DataTableV2DataRow } from '../types'

/**
 * Flattens grouped data by extracting subRows from group items
 */
export const flattenGroupedData = (items: DataTableV2DataRow[]): DataTableV2DataRow[] => {
  return items.reduce((acc: DataTableV2DataRow[], item) => {
    const itemWithSubRows = item as DataTableV2DataRow & { subRows?: DataTableV2DataRow[]; isGroup?: boolean }

    // If item is a group (has subRows), add the subRows (children) instead of the group row itself
    if (itemWithSubRows.subRows && Array.isArray(itemWithSubRows.subRows)) {
      // Recursive call in case of multi-level grouping
      acc.push(...flattenGroupedData(itemWithSubRows.subRows))
    } else if (!itemWithSubRows.isGroup) {
      // Only add non-group rows
      acc.push(item)
    }
    return acc
  }, [])
}

/**
 * Normalizes V2 data for an Excel worksheet.
 */
export const normalizeDataForExcelV2 = (data: DataTableV2DataRow[]): Record<string, unknown>[] => {
  // Process data to ensure it's flat and readable
  return data.map((row) => {
    const newRow: Record<string, unknown> = {}
    Object.keys(row).forEach((key) => {
      // Skip internal properties
      if (key === 'subRows' || key === 'isGroup') {
        return
      }

      const value = row[key]

      if (value === null || value === undefined) {
        newRow[key] = ''
      } else if (value instanceof Date) {
        newRow[key] = value.toLocaleDateString()
      } else if (Array.isArray(value)) {
        // Handle arrays - check if it's an array of title-value objects
        if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'title' in value[0]) {
          // Array of title-value objects - join titles
          newRow[key] = value.map((item) => (item as { title: string }).title).join(', ')
        } else {
          // Regular array - join values
          newRow[key] = value.join(', ')
        }
      } else if (typeof value === 'object' && value !== null) {
        // Handle title-value objects
        if ('title' in value && 'value' in value) {
          newRow[key] = (value as { title: string }).title
        } else {
          newRow[key] = JSON.stringify(value)
        }
      } else {
        newRow[key] = value
      }
    })
    return newRow
  })
}

/**
 * Exports data to an Excel file.
 */
export const exportToExcelV2 = async (data: DataTableV2DataRow[], fileName: string) => {
  const processedData = normalizeDataForExcelV2(data)
  const keys = processedData.length > 0 ? Object.keys(processedData[0]) : []
  const columnWidths = calculateExcelColumnWidths(keys, processedData, 50)
  const sheetData = createExcelSheetData(keys, processedData)

  await downloadExcelFile(sheetData, columnWidths, `${fileName || 'export'}`)
}
